/**
 * timeline.js - the one timeline (AL-01) as pure helpers: the filter chips
 * (AL-02), newest-first order and month groups on LOCAL days, the one-line
 * row (AL-08), and what the Log Activity form offers to pick: attendees
 * (AL-07) and opportunity tags (AL-09).
 *
 * Items come straight from GET /api/accounts/<id>/timeline (or the contact /
 * opportunity twins), fetched directly - so they are snake_case, never the
 * camelCased rows AppContext hands out. Shape: see Timeline.jsx.
 *
 * Kept free of React so __tests__/timeline.test.js can check it directly.
 *
 * NOT named timeline.js: on a case-insensitive disk (Windows, where the
 * build runs) `import Timeline from './Timeline'` finds Timeline.js before
 * Timeline.jsx, so a timeline.js here would hand the component's importers
 * this file and the page would crash. Timeline.jsx re-exports these helpers.
 */
import { REVISION_TYPE, pickField, isLeftCompany } from '../../data/crmStages';
import { accountLabel, indexById, opportunityLabel } from './nextStep';
import { matchCustomer } from '../../utils/customerMatch';
import { fmtDate } from '../../utils/format';
import { localDay as ymd } from '../../utils/localDay';

const lc = (s) =>
  String(s == null ? '' : s)
    .trim()
    .toLowerCase();

/* ── filter chips (AL-02) ────────────────────────────────────────────────── */

/**
 * Paul's order: Meetings / Emails / Stage and status changes / Quotes / Tasks.
 * `types` are the activity types each chip picks up. 'Other', a blank type
 * and anything unknown belong to no chip: they show only with none selected.
 */
export const CHIPS = [
  {
    key: 'meetings',
    label: 'Meetings',
    types: ['Discovery call', 'Follow-up call', 'Teams meeting', 'Site visit', 'Trade show', 'Customer Q&A'],
  },
  { key: 'emails', label: 'Emails', types: ['Email thread', 'Document sent', 'NDA sent / received'] },
  { key: 'changes', label: 'Stage and status changes', types: [] },
  { key: 'quotes', label: 'Quotes', types: ['RFQ received', 'Budgetary sent', 'Proposal sent'] },
  { key: 'tasks', label: 'Tasks', types: [] },
];

const TYPE_CHIP = new Map(CHIPS.flatMap((c) => c.types.map((t) => [lc(t), c.key])));

/** Colours per chip, for the type pill and the chip itself. */
export const CHIP_COLORS = {
  meetings: { fg: '#7B1FA2', bg: '#F6EFFA' },
  emails: { fg: '#1565C0', bg: '#E3F2FD' },
  changes: { fg: '#E65100', bg: '#FFF3E0' },
  quotes: { fg: '#1B5E20', bg: '#E8F5E9' },
  tasks: { fg: '#00695C', bg: '#E0F2F1' },
  other: { fg: '#555555', bg: '#F0F0F0' },
};

const oppField = (item) => {
  const f = item && item.opportunity && item.opportunity.field;
  if (f) return f;
  return item && item.type === 'Opportunity created' ? 'created' : null;
};

/** Which chip an item belongs to, or null (then it shows only under "all"). */
export function chipOf(item) {
  if (!item) return null;
  switch (item.kind) {
    case 'activity': {
      const t = item.activity && item.activity.type != null ? item.activity.type : item.type;
      return TYPE_CHIP.get(lc(t)) || null;
    }
    case 'stage':
    case 'designation':
    case 'lead_status':
      return 'changes';
    case 'task':
      return 'tasks';
    case 'opportunity':
      // a new opportunity is a quote step; its status / stage / site link are changes
      return oppField(item) === 'created' ? 'quotes' : 'changes';
    default:
      return null;
  }
}

/** Items under the selected chips (an array of chip keys). None selected = all. */
export function filterTimeline(items, selected) {
  const list = (items || []).filter(Boolean);
  const want = new Set(selected || []);
  if (!want.size) return list;
  return list.filter((it) => want.has(chipOf(it)));
}

/** How many items each chip would show: { meetings: 3, ... }. */
export function chipCounts(items) {
  const out = Object.fromEntries(CHIPS.map((c) => [c.key, 0]));
  for (const it of items || []) {
    const k = chipOf(it);
    if (k) out[k] += 1;
  }
  return out;
}

/* ── days, order and months ──────────────────────────────────────────────── */

/** 'YYYY-MM-DD' of a Date or timestamp in the browser's time zone ('' when unreadable). */
export function localDay(v) {
  if (v == null || v === '') return '';
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return '';
  return ymd(d);
}

/** Today, local. (new Date().toISOString() is the UTC day - tomorrow, most US evenings.) */
export const todayLocal = () => localDay(new Date());

/**
 * The day an item sorts and groups under: an activity's own date, otherwise
 * the LOCAL day of `at`. Never the UTC day off the timestamp string.
 */
export function itemDay(item) {
  const d = String((item && item.date) || '');
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.slice(0, 10);
  return localDay(item && item.at);
}

const atMs = (it) => {
  const t = new Date(it && it.at).getTime();
  return isNaN(t) ? 0 : t;
};

/** Newest first: by day, then by `at`; items with no day at all go last. */
export function sortTimeline(items) {
  return (items || [])
    .filter(Boolean)
    .slice()
    .sort((a, b) => {
      const da = itemDay(a);
      const db = itemDay(b);
      if (da !== db) {
        if (!da) return 1;
        if (!db) return -1;
        return da < db ? 1 : -1;
      }
      return atMs(b) - atMs(a) || String(a.id || '').localeCompare(String(b.id || ''));
    });
}

/** 'September 2026' for '2026-09'; 'No date' for the undated bucket. */
export function monthLabel(key) {
  if (!key || key === 'undated') return 'No date';
  const d = new Date(`${key}-01T00:00`);
  return isNaN(d.getTime()) ? key : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Month groups, newest month first, each newest first:
 * [{ key: '2026-09', label: 'September 2026', items: [...] }]. Items with no
 * day land in a last 'undated' group.
 */
export function groupByMonth(items) {
  const groups = [];
  const byKey = new Map();
  for (const it of sortTimeline(items)) {
    const day = itemDay(it);
    const key = day ? day.slice(0, 7) : 'undated';
    let g = byKey.get(key);
    if (!g) {
      g = { key, label: monthLabel(key), items: [] };
      byKey.set(key, g);
      groups.push(g);
    }
    g.items.push(it);
  }
  return groups;
}

/* ── the one-line row (AL-08) ────────────────────────────────────────────── */

const firstLine = (s) =>
  String(s || '')
    .split(/\r?\n/)
    .map((x) => x.trim())
    .find(Boolean) || '';

const KIND_TYPE = {
  activity: 'Note',
  stage: 'Stage change',
  designation: 'Designation change',
  lead_status: 'Status change',
  task: 'Task',
  opportunity: 'Opportunity',
};

/** Where an opportunity or task change came from, in words (null for an ordinary app edit).
 *  'sheet sync' rows were written by the old quote-list import, retired 10/1/26
 *  when GlobalOps became the source of truth; they read as "imported". */
export function sourceLabel(source) {
  if (source === 'sheet sync') return 'imported';
  if (source === 'migration') return 'data update';
  return null;
}

/* The actor the old quote-list import wrote on its history rows. It is not a
   person, so it is never shown as one; the source label says "imported". */
const IMPORT_ACTORS = new Set(['excel sync']);
/** Who made a change, as shown (blank for the retired import's actor). */
export function whoLabel(who) {
  const w = String(who == null ? '' : who).trim();
  return IMPORT_ACTORS.has(w.toLowerCase()) ? '' : w;
}

const nameOfAccount = (id, accountsById) => {
  if (!id) return '';
  const a = accountsById && accountsById.get(String(id));
  return a ? accountLabel(a, accountsById) : String(id);
};

/** "from → to" for an opportunity change; the site link names the sites. */
function opportunityChange(item, accountsById) {
  const field = oppField(item);
  if (field === 'created') return item.to ? `status ${item.to}` : '';
  if (field === 'account_id') {
    return `${nameOfAccount(item.from, accountsById) || 'no site'} → ${nameOfAccount(item.to, accountsById) || 'no site'}`;
  }
  if (item.from == null && item.to == null) return '';
  return `${item.from || 'none'} → ${item.to || 'none'}`;
}

/**
 * What one collapsed row shows:
 *   { day, date (mm/dd/yyyy), type, rev, typeLabel ('Proposal sent · Rev 2'),
 *     subject, change, who, where, files, chip }
 * `ctx.accountsById` names sites; `ctx.homeId` is the record the timeline is
 * open on (an entry that lives somewhere else gets that place as its label).
 */
export function rowSummary(item, ctx = {}) {
  const it = item || {};
  const act = it.activity || null;
  const day = itemDay(it);
  const revRaw = act && act.revision != null ? act.revision : it.revision;
  const rev = revRaw != null && String(revRaw).trim() !== '' ? String(revRaw).trim() : '';
  const type = it.type || KIND_TYPE[it.kind] || '';
  let subject = String(it.subject || '').trim();
  let change = '';
  if (it.kind === 'activity') {
    if (!subject) subject = firstLine(it.text != null ? it.text : act && act.text);
  } else if (it.kind === 'stage') {
    subject = it.from ? `${it.from} → ${it.to || 'no stage'}` : `Added to the pipeline at ${it.to || 'no stage'}`;
  } else if (it.kind === 'designation' || it.kind === 'lead_status') {
    subject = it.from ? `${it.from} → ${it.to || 'none'}` : `Set to ${it.to || 'none'}`;
  } else if (it.kind === 'opportunity') {
    if (!subject) subject = (it.opportunity && it.opportunity.label) || (it.opportunity && it.opportunity.id) || 'Opportunity';
    change = opportunityChange(it, ctx.accountsById);
  }
  // the site (on its company), or where the entry lives when it is not this record
  let where = it.site_name || '';
  if (!where && (it.kind === 'activity' || it.kind === 'task') && it.account_id && String(it.account_id) !== String(ctx.homeId || '')) {
    where = nameOfAccount(it.account_id, ctx.accountsById);
  }
  const files = act && Array.isArray(act.attachments) ? act.attachments.length : 0;
  return {
    day,
    date: fmtDate(day),
    type,
    rev,
    typeLabel: `${type}${rev ? ` · Rev ${rev}` : ''}`,
    subject,
    change,
    who: whoLabel(it.who),
    where,
    files,
    chip: chipOf(it),
  };
}

/** A timestamp as the viewer's local date and time: '09/28/2026, 3:03 PM'. */
export function localDateTime(v) {
  const d = new Date(v);
  if (!v || isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** A task's status now, in words ('Deleted' when the task is gone). */
export function taskStatusLabel(status) {
  if (status == null) return 'Deleted';
  return { open: 'Open', complete: 'Completed', cancelled: 'Cancelled' }[status] || String(status);
}

/* ── pickers for the Log Activity form ───────────────────────────────────── */

/**
 * The record and, for a company, its sites - read live from allAccounts so a
 * just-added contact or site shows. [] when there is no record.
 */
export function familyOf(record, allAccounts) {
  if (!record || record.id == null) return [];
  const id = String(record.id);
  const live = (allAccounts || []).find((a) => a && String(a.id) === id) || record;
  const sites = (allAccounts || []).filter((a) => a && String(pickField(a, 'parentAccountId', 'parent_account_id') || '') === id);
  return [live, ...sites];
}

/**
 * AL-09: the opportunities an entry on this record can be tagged to - the
 * family's. The explicit link wins: a row linked to one of the family's
 * records counts, a row linked to ANY other record never does, and only an
 * unlinked row is matched on its Customer name. A site also takes its
 * company's unlinked rows (matched on the company's key or name): most rows
 * from before A-02 name the company, not the plant. Open ones first, then
 * newest. Returns [{ id, label: 'Q5365 · Name', status, open }].
 */
export function taggableOpportunities(record, allAccounts, allProjects) {
  const family = familyOf(record, allAccounts);
  if (!family.length) return [];
  const ids = new Set(family.map((a) => String(a.id)));
  const parentId = pickField(family[0], 'parentAccountId', 'parent_account_id');
  const parent = parentId ? (allAccounts || []).find((a) => a && String(a.id) === String(parentId)) : null;
  // the parent adds a name to match on, never its id: a row linked to the company itself is another record's
  const keys = [...family, ...(parent ? [parent] : [])]
    .map((a) => pickField(a, 'opportunityKey', 'opportunity_key') || a.name)
    .filter(Boolean);
  const out = [];
  for (const p of allProjects || []) {
    if (!p || p.id == null) continue;
    const link = pickField(p, 'accountId', 'account_id');
    const mine = link ? ids.has(String(link)) : keys.some((k) => matchCustomer(k, p.customer));
    if (!mine) continue;
    out.push({
      id: String(p.id),
      label: opportunityLabel(p),
      status: p.status || '',
      open: lc(p.status) === 'open',
      day: String(pickField(p, 'quoteDate', 'quote_date') || '').slice(0, 10),
    });
  }
  return out
    .sort((a, b) => Number(b.open) - Number(a.open) || b.day.localeCompare(a.day) || a.label.localeCompare(b.label))
    .map(({ day, ...o }) => o);
}

/**
 * AL-07: the customer contacts who can be picked as attendees - the family's
 * (a company lists its sites' people too; a site also gets its company's
 * corporate contacts), never a Left company contact (C-04). Primary first,
 * then the record's own, then by name.
 * Returns [{ id, name, label, where, contact }].
 */
export function attendeeContacts(record, allAccounts) {
  const family = familyOf(record, allAccounts);
  if (!family.length) return [];
  const [self, ...sites] = family;
  const siteNames = new Map(sites.map((s) => [String(s.id), s.name || '']));
  const byId = indexById(allAccounts);
  const parentId = pickField(self, 'parentAccountId', 'parent_account_id');
  const parent = parentId ? byId.get(String(parentId)) : null;
  const seen = new Set();
  const out = [];
  const add = (c, where, rank) => {
    if (!c || c.id == null || isLeftCompany(c) || seen.has(String(c.id))) return;
    seen.add(String(c.id));
    const role = pickField(c, 'contactRole', 'contact_role');
    out.push({
      id: String(c.id),
      name: c.name || 'Unnamed',
      label: `${c.name || 'Unnamed'}${role ? ` · ${role}` : ''}`,
      where,
      rank,
      primary: !!pickField(c, 'isPrimary', 'is_primary'),
      contact: c,
    });
  };
  // a company's list from GET /api/accounts already carries its sites' people, tagged site_id
  for (const c of self.contacts || []) {
    const sid = pickField(c, 'siteId', 'site_id');
    add(c, sid ? pickField(c, 'siteName', 'site_name') || siteNames.get(String(sid)) || '' : '', sid ? 1 : 0);
  }
  for (const s of sites) for (const c of s.contacts || []) add(c, s.name || '', 1);
  if (parent) {
    for (const c of parent.contacts || []) {
      if (!pickField(c, 'siteId', 'site_id')) add(c, `${parent.name || 'company'} (company)`, 2);
    }
  }
  return out.sort((a, b) => Number(b.primary) - Number(a.primary) || a.rank - b.rank || a.name.localeCompare(b.name));
}

/* ── the form's body and its files ───────────────────────────────────────── */

const idList = (v) => Array.from(new Set((Array.isArray(v) ? v : []).map((x) => String(x == null ? '' : x).trim()).filter(Boolean)));

/**
 * The Log Activity form -> the JSON body for POST / PUT
 * /api/accounts/<id>/activity. The revision only means something on a
 * proposal (AL-05), so any other type clears it. `nextStep` ({description,
 * owner, due_date}) goes only on a new entry that asked for one (AL-06).
 */
export function activityBody(f, { nextStep } = {}) {
  const body = {
    date: f.date,
    text: f.text,
    type: f.type || null,
    subject: f.subject || null,
    recording_url: f.recording_url || null,
    revision: f.type === REVISION_TYPE && String(f.revision || '').trim() ? String(f.revision).trim() : null,
    contact_ids: idList(f.contact_ids),
    internal_attendees: idList(f.internal_attendees),
    opportunity_ids: idList(f.opportunity_ids),
  };
  if (nextStep) body.next_step = nextStep;
  return body;
}

/** A stored entry (the timeline's `activity`, either casing) -> the form's fields. */
export function formFromEntry(a) {
  const e = a || {};
  return {
    text: e.text || '',
    date: String(e.date || '').slice(0, 10),
    type: e.type || '',
    subject: e.subject || '',
    recording_url: pickField(e, 'recordingUrl', 'recording_url') || '',
    revision: e.revision == null ? '' : String(e.revision),
    contact_ids: idList(pickField(e, 'contactIds', 'contact_ids')),
    internal_attendees: idList(pickField(e, 'internalAttendees', 'internal_attendees')),
    opportunity_ids: idList(pickField(e, 'opportunityIds', 'opportunity_ids')),
  };
}

/** AL-07: the file types an activity entry takes (the server holds the same list). */
export const ACTIVITY_FILE_EXTS = [
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'heic',
  'bmp',
  'tif',
  'tiff',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'xlsm',
  'csv',
  'ppt',
  'pptx',
  'txt',
  'rtf',
  'msg',
  'eml',
  'zip',
  '7z',
  'dwg',
  'dxf',
  'step',
  'stp',
  'igs',
  'iges',
  'mp4',
  'mov',
];
const EXT_SET = new Set(ACTIVITY_FILE_EXTS);
export const FILE_ACCEPT = ACTIVITY_FILE_EXTS.map((x) => `.${x}`).join(',');

/** Can a file with this name be attached to an activity entry? */
export function fileAllowed(name) {
  const m = String(name || '')
    .toLowerCase()
    .match(/\.([a-z0-9]+)$/);
  return !!m && EXT_SET.has(m[1]);
}

/** '312 KB', '4.2 MB' - for a file row. */
export function fileSize(bytes) {
  const n = Number(bytes);
  if (!isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Keep `ids` in `list` order-stable: toggling one id on or off. */
export function toggleId(ids, id) {
  const s = String(id);
  const list = idList(ids);
  return list.includes(s) ? list.filter((x) => x !== s) : [...list, s];
}

/** The given option list plus any already-picked value it lacks (so an edit never drops one silently). */
export function withPicked(options, picked, describe) {
  const have = new Set((options || []).map((o) => String(o.id)));
  const extra = idList(picked)
    .filter((id) => !have.has(id))
    .map((id) => ({ id, ...describe(id), extra: true }));
  return [...(options || []), ...extra];
}
